import { useCallback, useEffect, useMemo, useState } from 'react'
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore'
import { useWorkspace } from '../context/useWorkspace'
import { roleCanPerform } from '../utils/workspacePermissions'
import { firestoreDb } from '../services/firebase'
import { isNoSignInPreviewActive } from '../debug'
import { testingOverrideAllowsAllFeatures } from '../config/testingOverrides'
import {
  buildLinkGatewayUrl,
  checkIntegrationReadiness,
  recordIntegrationGateHit,
} from '../services/integrationGatewayService'
import { isOperationalConnectionStatus, type ConnectionMetadata, type IntegrationInterceptorProps, type IntegrationProviderId, type IntegrationReadinessCode } from '../types/integration'
import type { IntegrationProvider, StorageConnectionMetadata, StorageConnectionProvider } from '../types/schema'

const PROVIDER_LABELS: Record<IntegrationProviderId, string> = {
  G_DRIVE: 'Google Drive',
  NOTION: 'Notion',
  JIRA: 'Jira',
  GOOGLE_CALENDAR: 'Google Calendar',
  GOOGLE_SHEETS: 'Google Sheets',
  GENERIC_WEBHOOK: 'Generic Webhook',
  SLACK: 'Slack',
  FIGMA: 'Figma',
  ZOOM: 'Zoom',
  DROPBOX: 'Dropbox',
  GITHUB: 'GitHub',
  STAYS_PMS: 'Hotel PMS',
}
const GATE_DEDUPE_MS = 30_000
const recentGateHits = new Map<string, number>()

export type IntegrationGatewayOptions = {
  actionKey?: string
  connectionId?: string
  providerLabel?: string
  recordGateHit?: boolean
}

export type IntegrationActionHandler = <T>(action: () => T | Promise<T>) => () => Promise<T | undefined>

export type IntegrationGateway = {
  isReady: boolean
  loading: boolean
  connection: ConnectionMetadata | null
  error: Error | null
  statusCode: IntegrationReadinessCode
  refresh: () => Promise<void>
  handleAction: IntegrationActionHandler
  interceptorProps: IntegrationInterceptorProps
}

type GatewayState = {
  key: string
  connection: ConnectionMetadata | null
  authoritativeConnectionId: string | null
  error: Error | null
  statusCode: IntegrationReadinessCode
}

export const useIntegrationGateway = (
  providerId: IntegrationProviderId | null,
  options: IntegrationGatewayOptions = {},
): IntegrationGateway => {
  const { activeWorkspaceId, activeRole, appMode, canPerform, workspaces } = useWorkspace()
  const workspace = workspaces.find((candidate) => candidate.id === activeWorkspaceId)
  const canRead = appMode === 'TEAM' && Boolean(activeWorkspaceId) && canPerform('integrations.view')
  const key = canRead && providerId ? `${activeWorkspaceId}:${providerId}:${options.connectionId ?? ''}` : ''
  const [state, setState] = useState<GatewayState>({ key: '', connection: null, authoritativeConnectionId: null, error: null, statusCode: 'AUTH_REQUIRED' })
  const [listenerGeneration, setListenerGeneration] = useState(0)
  const [interceptorOpen, setInterceptorOpen] = useState(false)

  useEffect(() => {
    if (!key || !providerId) return
    if (isNoSignInPreviewActive()) {
      setState({ key, connection: null, authoritativeConnectionId: null, error: null, statusCode: 'AUTH_REQUIRED' })
      return
    }
    let active = true
    const constraints = [
      where('workspaceId', '==', activeWorkspaceId),
      where('providerId', '==', providerId),
    ]
    const stop = onSnapshot(query(collection(firestoreDb, 'connection_metadata'), ...constraints), (snapshot) => {
      const documents = snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as ConnectionMetadata)
      void checkIntegrationReadiness({ workspaceId: activeWorkspaceId, providerId, connectionId: options.connectionId })
        .then((readiness) => {
          if (!active) return
          const connection = readiness.connectionId
            ? documents.find((entry) => entry.id === readiness.connectionId) ?? null
            : options.connectionId ? documents.find((entry) => entry.id === options.connectionId) ?? null : documents[0] ?? null
          setState({ key, connection, authoritativeConnectionId: readiness.connectionId, error: null, statusCode: readiness.statusCode })
        })
        .catch((error: unknown) => {
          if (!active) return
          setState({ key, connection: null, authoritativeConnectionId: null, error: error instanceof Error ? error : new Error('Integration readiness check failed.'), statusCode: 'CHECK_FAILED' })
        })
    }, (error) => setState({ key, connection: null, authoritativeConnectionId: null, error, statusCode: 'CHECK_FAILED' }))
    return () => {
      active = false
      stop()
    }
  }, [activeWorkspaceId, key, listenerGeneration, options.connectionId, providerId])

  const currentState = state.key === key ? state : null
  const isReady = Boolean(
    currentState?.connection
      && isOperationalConnectionStatus(currentState.connection.status)
      && currentState.connection.tokenRef === `storage_secrets/${currentState.connection.id}`
      && currentState.authoritativeConnectionId === currentState.connection.id,
  )
  const actionKey = options.actionKey?.trim() || 'INTEGRATION_ACTION'

  const refresh = useCallback(async () => {
    if (!providerId || !activeWorkspaceId || !canRead) return
    if (isNoSignInPreviewActive()) {
      setState((current) => current.key === key ? { ...current, authoritativeConnectionId: null, statusCode: 'AUTH_REQUIRED', error: null } : current)
      return
    }
    try {
      const readiness = await checkIntegrationReadiness({ workspaceId: activeWorkspaceId, providerId, connectionId: options.connectionId, forceRefresh: true })
      setState((current) => current.key === key ? { ...current, authoritativeConnectionId: readiness.connectionId, statusCode: readiness.statusCode, error: null } : current)
    } catch (error) {
      setState((current) => current.key === key ? { ...current, error: error instanceof Error ? error : new Error('Integration readiness refresh failed.'), statusCode: 'CHECK_FAILED' } : current)
    } finally {
      setListenerGeneration((current) => current + 1)
    }
  }, [activeWorkspaceId, canRead, key, options.connectionId, providerId])

  const handleAction = useCallback<IntegrationActionHandler>((action) => async () => {
    if (!providerId || !activeWorkspaceId || !canRead) {
      setInterceptorOpen(true)
      return undefined
    }
    try {
      const readiness = await checkIntegrationReadiness({
        workspaceId: activeWorkspaceId,
        providerId,
        connectionId: options.connectionId,
      })
      if (readiness.isReady) return await action()
    } catch {
      // Readiness failures are deliberately projected as a closed gate.
    }
    setInterceptorOpen(true)
    const dedupeKey = `${activeWorkspaceId}:${providerId}:${actionKey}`
    const lastHit = recentGateHits.get(dedupeKey) ?? 0
    if (options.recordGateHit !== false && Date.now() - lastHit >= GATE_DEDUPE_MS) {
      recentGateHits.set(dedupeKey, Date.now())
      void recordIntegrationGateHit({ workspaceId: activeWorkspaceId, providerId, actionKey }).catch(() => undefined)
    }
    return undefined
  }, [activeWorkspaceId, actionKey, canRead, options.connectionId, options.recordGateHit, providerId, setInterceptorOpen])

  const returnTo = typeof window === 'undefined' ? '/' : `${window.location.pathname}${window.location.search}${window.location.hash}`
  const targetUrl = workspace?.slug && providerId ? buildLinkGatewayUrl(workspace.slug, providerId, returnTo) : '/'
  const interceptorProps = useMemo<IntegrationInterceptorProps>(() => ({
    providerLabel: options.providerLabel ?? (providerId ? PROVIDER_LABELS[providerId] : 'Provider'),
    isReady,
    open: interceptorOpen,
    targetUrl,
    adminRequired: !roleCanPerform(activeRole, 'ADMIN') && !testingOverrideAllowsAllFeatures(),
    onOpenChange: setInterceptorOpen,
  }), [activeRole, interceptorOpen, isReady, options.providerLabel, providerId, setInterceptorOpen, targetUrl])

  return {
    isReady,
    loading: Boolean(key) && !currentState,
    connection: currentState?.connection ?? null,
    error: currentState?.error ?? null,
    statusCode: currentState?.statusCode ?? 'AUTH_REQUIRED',
    refresh,
    handleAction,
    interceptorProps,
  }
}

export type ConnectableProvider = IntegrationProvider | StorageConnectionProvider
type LegacyReadiness = { ready: boolean; loading: boolean; connection: StorageConnectionMetadata | null; error: Error | null }

const useLegacyConnection = (providerId: ConnectableProvider): LegacyReadiness => {
  const { activeWorkspaceId, appMode, canPerform } = useWorkspace()
  const canRead = appMode === 'TEAM' && Boolean(activeWorkspaceId) && canPerform('integrations.view')
  const key = canRead ? `${activeWorkspaceId}:${providerId}` : ''
  const [state, setState] = useState<LegacyReadiness & { key: string }>({ key: '', ready: false, loading: true, connection: null, error: null })
  useEffect(() => {
    if (!key) return
    return onSnapshot(query(collection(firestoreDb, 'storage_connections'), where('workspaceId', '==', activeWorkspaceId), where('provider', '==', providerId), where('status', '==', 'ACTIVE'), limit(1)), (snapshot) => {
      const entry = snapshot.docs[0]
      const connection = entry ? ({ id: entry.id, ...entry.data() } as StorageConnectionMetadata) : null
      setState({ key, ready: Boolean(connection), loading: false, connection, error: null })
    }, (error) => setState({ key, ready: false, loading: false, connection: null, error }))
  }, [activeWorkspaceId, key, providerId])
  if (!key) return { ready: false, loading: false, connection: null, error: null }
  if (state.key !== key) return { ready: false, loading: true, connection: null, error: null }
  return state
}

export const useCheckConnection = (providerId: ConnectableProvider): LegacyReadiness => {
  const mapped: IntegrationProviderId | null = providerId === 'NOTION' ? 'NOTION' : providerId === 'JIRA' ? 'JIRA' : null
  const gateway = useIntegrationGateway(mapped, { actionKey: 'LEGACY_CONNECTION_CHECK' })
  const legacy = useLegacyConnection(providerId)
  if (mapped) return { ready: gateway.isReady, loading: gateway.loading, connection: null, error: gateway.error }
  return legacy
}

export type GlobalConnectionStatus = {
  loading: boolean
  totalConnections: number
  activeCount: number
  erroredCount: number
  tone: 'healthy' | 'degraded' | 'offline' | 'idle'
  byProvider: Record<string, ConnectionMetadata>
}

export const useGlobalConnectionStatus = (): GlobalConnectionStatus => {
  const { activeWorkspaceId, appMode, canPerform } = useWorkspace()
  const canRead = appMode === 'TEAM' && Boolean(activeWorkspaceId) && canPerform('integrations.view')
  const key = canRead ? activeWorkspaceId : ''
  const [state, setState] = useState<{ key: string; connections: ConnectionMetadata[] }>({ key: '', connections: [] })
  useEffect(() => {
    if (!key) return
    return onSnapshot(query(collection(firestoreDb, 'connection_metadata'), where('workspaceId', '==', activeWorkspaceId)), (snapshot) => {
      setState({ key, connections: snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as ConnectionMetadata) })
    })
  }, [activeWorkspaceId, key])
  return useMemo(() => {
    const connections = state.key === key ? state.connections : []
    const byProvider: Record<string, ConnectionMetadata> = {}
    let activeCount = 0
    let erroredCount = 0
    connections.forEach((connection) => {
      byProvider[connection.providerId] = connection
      if (isOperationalConnectionStatus(connection.status)) activeCount += 1
      if (connection.status === 'EXPIRED') erroredCount += 1
    })
    const totalConnections = connections.length
    const tone: GlobalConnectionStatus['tone'] = totalConnections === 0 ? 'idle' : erroredCount ? 'degraded' : activeCount ? 'healthy' : 'offline'
    return { loading: Boolean(key) && state.key !== key, totalConnections, activeCount, erroredCount, tone, byProvider }
  }, [key, state.connections, state.key])
}
